/**
 * Shared saved-plan vs last-build-handoff comparison.
 *
 * These markers (`lastBuiltPlanRevision` / `lastBuiltPlanHash`) are written when
 * a plan is handed to the build pipeline, BEFORE `completeBuild` may mark the
 * build terminal — so callers decide separately whether to use "sent" or "built"
 * copy, and a caller that needs "this plan produced a FINISHED build" must pair
 * these predicates with a terminal interview status
 * (`isTerminalBuildGameInterviewStatus`).
 *
 * Canonical home (crucible#3571): these predicates were renderer-only, in
 * `crucible-ui/src/components/plan-state.ts`. The main-process build-kickoff
 * guard needs the SAME comparison, and a main-side copy would be free to drift
 * from the renderer's — the exact class of split-brain that let a
 * built-game kickoff be accepted in the first place. `crucible-ui`'s module now
 * re-exports from here so there is one implementation and one set of tests.
 */
/** A plan has been handed to the build pipeline at least once. */
export function hasBuiltRevision(c) {
    return c.lastBuiltPlanRevision !== undefined || c.lastBuiltPlanHash !== undefined;
}
/**
 * True when the saved plan differs from the last-built plan. Hash comparison
 * wins when both sides carry hashes (revision counters can drift across
 * restore/import); falls back to revision numbers.
 */
export function savedPlanDiffersFromBuilt(c) {
    const revisionDiffers = c.planRevision !== undefined &&
        c.lastBuiltPlanRevision !== undefined &&
        c.lastBuiltPlanRevision !== c.planRevision;
    const hashDiffers = c.planHash !== undefined &&
        c.lastBuiltPlanHash !== undefined &&
        c.lastBuiltPlanHash !== c.planHash;
    return c.planHash !== undefined && c.lastBuiltPlanHash !== undefined
        ? hashDiffers
        : c.planRevision !== undefined && revisionDiffers;
}
export function savedPlanMatchesBuilt(c) {
    return hasBuiltRevision(c) && !savedPlanDiffersFromBuilt(c);
}
//# sourceMappingURL=plan-built-comparison.js.map